import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import {
  Image,
  FileText,
  Code,
  MessageSquare,
  Inbox,
  BookOpen,
  Webhook,
  Cpu,
  Globe,
  Zap,
  Database,
  Eye,
  BarChart,
  DollarSign,
  Smile,
  Building2,
  Megaphone,
  GitBranch,
  Users,
  LogOut,
  Clock,
  HelpCircle,
  Sliders,
  Sparkles,
  Plus,
  MoreHorizontal,
  Trash2,
  Copy,
  Layers,
  ChevronDown,
  X,
} from "lucide-react";
import api from "../utils/api";

// Sleek Chevron Icon for dropdown arrows and collapsible headers
const IconChevron = ({ open, className = "" }) => (
  <ChevronDown
    size={13}
    className={`transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"} ${className}`}
  />
);

const Sidebar = ({ 
  activeTab, 
  setActiveTab, 
  isCollapsed, 
  setIsCollapsed, 
  activeChatsCount = 0,
  activeProduct = "ai-agents",
  setActiveProduct
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Switcher states & flow list states
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [flowMenuOpenId, setFlowMenuOpenId] = useState(null);
  const [flowActionLoading, setFlowActionLoading] = useState(null);

  const switcherRef = useRef(null);

  // Auto-close sidebar on mobile when navigating
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768 && setIsCollapsed) {
      setIsCollapsed(true);
    }
  }, [location.pathname, location.search, setIsCollapsed]);

  // Auto-collapse on initial mount if mobile & handle screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && !isCollapsed && setIsCollapsed) {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed, setIsCollapsed]);

  // Close workspace switcher when clicking/tapping outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setIsSwitcherOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsSwitcherOpen(false);
      }
    };
    if (isSwitcherOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSwitcherOpen]);

  const handleNavClick = (id) => {
    hideItemTooltip();
    if (setActiveTab && id) {
      setActiveTab(id);
    }
    if (typeof window !== "undefined" && window.innerWidth < 768 && setIsCollapsed) {
      setIsCollapsed(true);
    }
  };

  // Auto-detect flows product if URL starts with /flow-builder or activeProduct is 'flows'
  const effectiveProduct = (location.pathname.startsWith("/flow-builder") || activeProduct === "flows") ? "flows" : "ai-agents";

  // Get user role from session
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';

  const fetchFlows = useCallback(async () => {
    setLoadingFlows(true);
    try {
      const data = await api.getBotFlows();
      setFlows(data || []);
    } catch (err) {
      console.error("Failed to fetch flows for sidebar:", err);
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  useEffect(() => {
    if (effectiveProduct === "flows") {
      fetchFlows();
    }
  }, [effectiveProduct, fetchFlows]);

  const handleDeleteFlow = async (e, flowId) => {
    e.preventDefault();
    e.stopPropagation();
    setFlowMenuOpenId(null);
    setFlowActionLoading(flowId);
    try {
      await api.deleteBotFlow(flowId);
      await fetchFlows();
      if (location.search.includes(`id=${flowId}`)) {
        navigate("/flow-builder");
      }
    } catch (err) {
      console.error("Failed to delete flow:", err);
    } finally {
      setFlowActionLoading(null);
    }
  };

  const handleDuplicateFlow = async (e, flow) => {
    e.preventDefault();
    e.stopPropagation();
    setFlowMenuOpenId(null);
    setFlowActionLoading(flow.id);
    try {
      const detail = await api.getBotFlowDetail(flow.id);
      await api.saveBotFlow({
        name: `${detail.name} (Copy)`,
        description: detail.description || '',
        content: detail.content || '{}',
        keywords: detail.keywords || [],
        is_active: true,
      });
      await fetchFlows();
    } catch (err) {
      console.error("Failed to duplicate flow:", err);
    } finally {
      setFlowActionLoading(null);
    }
  };

  // Tooltip state for collapsed sidebar (renders in a fixed portal so it floats over all pages)
  const [tooltip, setTooltip] = useState({ visible: false, text: "", top: 0, left: 0 });

  const showItemTooltip = (e, text) => {
    if (!isCollapsed || (typeof window !== "undefined" && window.innerWidth < 768)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const hideItemTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  // Grouped sections matching sevag reference & modern layout
  const sections = [
    {
      title: "",
      items: [
        { id: "human-agent-chat", label: "Live Chat", icon: MessageSquare, path: "/human-agent-chat", type: "single", hasBadge: true }
      ]
    },
    {
      title: "BOT BUILDER",
      items: [
        { id: "instructions", label: "AI Agent Instructions", icon: Sliders, path: "/instructions", type: "single" },
        { id: "skills", label: "Skills", icon: Sparkles, path: "/skills", type: "single" },
        { id: "business-flows", label: "Business Flows", icon: GitBranch, path: "/business-flows", type: "single" },
        { id: "api-tools", label: "API Tools", icon: Code, path: "/api-tools", type: "single" },
        {
          id: "sources",
          label: "Sources",
          icon: Database,
          type: "dropdown",
          subItems: [
            { id: "documents", label: "Knowledge Base", path: "/documents", icon: FileText },
            { id: "knowledge-graph", label: "Knowledge Graph", path: "/knowledge-graph", icon: Database },
            { id: "smart-faq", label: "Smart FAQ Builder", path: "/smart-faq", icon: BookOpen }
          ]
        }
      ]
    },
    {
      title: "COMMUNICATION",
      items: [
        { id: "chat", label: "Copilot", icon: Inbox, path: "/chat", type: "single" },
        { id: "campaigns", label: "Campaigns", icon: Megaphone, path: "/campaigns", type: "single" },
        { id: "contacts", label: "Contacts", icon: Users, path: "/contacts", type: "single" },
        { id: "segments", label: "Segments", icon: Layers, path: "/segments", type: "single" },
        { id: "reports", label: "Reports", icon: BarChart, path: "/reports", type: "single" }
      ]
    },
    {
      title: "ANALYTICS",
      items: [
        { id: "analytics-overview", label: "Overview", path: "/analytics?tab=overview", icon: BarChart, type: "single" },
        { id: "analytics-tokens", label: "Tokens", path: "/analytics?tab=token-analytics", icon: Cpu, type: "single" },
        { id: "analytics-cost", label: "Cost Breakdown", path: "/analytics?tab=cost-analytics", icon: DollarSign, type: "single" },
        { id: "analytics-sessions", label: "Sessions", path: "/analytics?tab=sessions", icon: Clock, type: "single" },
        { id: "analytics-conversations", label: "Conversations", path: "/analytics?tab=conversations", icon: MessageSquare, type: "single" },
        { id: "analytics-sentiment", label: "Sentiment", path: "/analytics?tab=sentiment", icon: Smile, type: "single" },
        { id: "analytics-evaluations", label: "AI Evaluations", path: "/evaluations", icon: Zap, type: "single" }
      ]
    },
    {
      title: "SETTINGS",
      items: [
        {
          id: "workspace",
          label: "Workspace",
          icon: Building2,
          type: "dropdown",
          subItems: [
            { id: "ai-agents", label: "AI Agents", path: "/ai-agents", icon: Building2 },
            { id: "human-agents", label: "Human Agents", path: "/human-agents", icon: Users },
            { id: "human-agent-groups", label: "Human Agent Groups", path: "/human-agent-groups", icon: Building2 },
            { id: "logo", label: "Logo Management", path: "/logo", icon: Image },
            { id: "domains", label: "Domain Management", path: "/domains", icon: Globe },
            { id: "widget", label: "Widget Integration", path: "/widget", icon: Code },
            { id: "widget-preview", label: "Widget Preview", path: "/widget-preview", icon: Eye }
          ]
        },
        { id: "user-management", label: "Teams & Roles", icon: Users, path: "/user-management", type: "single" },
        { id: "billing", label: "Billing & Plans", icon: DollarSign, path: "/billing", type: "single" },
        { id: "integrations", label: "Integrations", icon: Zap, path: "/integrations", type: "single" },
        { id: "models", label: "Models", icon: Cpu, path: "/models", type: "single" },
        { id: "webhooks", label: "Webhooks", icon: Webhook, path: "/webhooks", type: "single" }
      ]
    },
    {
      title: "SUPPORT",
      items: [
        { id: "docs", label: "Help Center", icon: HelpCircle, path: "/docs", type: "single" }
      ]
    }
  ];

  // RBAC filter: Agents only see the Live Chat route
  const filteredSections = sections.map(section => {
    return {
      ...section,
      items: section.items.filter(item => {
        if (!isAdmin) {
          return item.id === "human-agent-chat";
        }
        return true;
      })
    };
  }).filter(section => section.items.length > 0);

  // Initialize Collapsible Section states (with auto-expansion of current active section)
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {
      "BOT BUILDER": true,
      "COMMUNICATION": true,
      "ANALYTICS": true,
      "SETTINGS": true,
      "SUPPORT": true
    };
    
    sections.forEach(sec => {
      if (sec.title) {
        const hasActiveItem = sec.items.some(item => {
          if (item.type === "single") {
            return location.pathname === item.path || (item.path && item.path.includes("?") && location.pathname + location.search === item.path);
          }
          if (item.type === "dropdown") {
            return item.subItems.some(sub => location.pathname === sub.path);
          }
          return false;
        });
        if (hasActiveItem) {
          initial[sec.title] = true;
        }
      }
    });
    
    return initial;
  });

  const toggleSection = (title) => {
    if (!title) return;
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Dropdown States for nested menus inside Bot Builder and Settings
  const isSourcesActive = filteredSections.find(s => s.title === "BOT BUILDER")?.items
    .find(i => i.id === "sources")?.subItems?.some(sub => location.pathname === sub.path);
  const [sourcesOpen, setSourcesOpen] = useState(!!isSourcesActive);

  const isWorkspaceActive = filteredSections.find(s => s.title === "SETTINGS")?.items
    .find(i => i.id === "workspace")?.subItems?.some(sub => location.pathname === sub.path);
  const [workspaceOpen, setWorkspaceOpen] = useState(!!isWorkspaceActive);

  const toggleDropdown = (id) => {
    if (id === "sources") setSourcesOpen(!sourcesOpen);
    if (id === "workspace") setWorkspaceOpen(!workspaceOpen);
  };

  const isDropdownOpen = (id) => {
    if (id === "sources") return sourcesOpen;
    if (id === "workspace") return workspaceOpen;
    return false;
  };

  // Helper check for active sub-items with exact search/query matching
  const isPathActive = (path) => {
    if (!path) return false;
    return location.pathname + location.search === path || 
      (path === "/analytics?tab=overview" && location.pathname === "/analytics" && !location.search);
  };

  const handleProductSwitch = (product) => {
    if (setActiveProduct) {
      setActiveProduct(product);
    } else {
      localStorage.setItem("activeProduct", product);
    }
    setIsSwitcherOpen(false);
    if (typeof window !== "undefined" && window.innerWidth < 768 && setIsCollapsed) {
      setIsCollapsed(true);
    }
    if (product === "flows") {
      navigate("/flow-builder");
    } else {
      navigate("/admin");
    }
  };

  const handleCreateNewFlow = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768 && setIsCollapsed) {
      setIsCollapsed(true);
    }
    navigate("/flow-builder/new");
  };

  return (
    <>
      {/* Mobile backdrop when sidebar is open on small screens */}
      {!isCollapsed && (
        <div
          onClick={() => setIsCollapsed(true)}
          className="fixed inset-0 bg-black/40 backdrop-blur-xs z-[190] md:hidden transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      <aside
        className={`${
          isCollapsed ? "-translate-x-full md:translate-x-0 md:w-[68px]" : "translate-x-0 w-72 max-w-[85vw] md:w-56"
        } fixed md:static inset-y-0 left-0 z-[200] md:z-auto flex flex-col bg-white border-r border-zinc-200/80 shrink-0 transition-all duration-300 ease-in-out select-none font-['Inter',sans-serif] shadow-2xl md:shadow-none`}
      >
        {/* Header: Logo & Collapse Toggle */}
        <div className={`h-14 border-b border-zinc-100 flex items-center shrink-0 ${isCollapsed ? 'justify-center px-0' : 'justify-between px-3.5'}`}>
          {!isCollapsed && (
            <NavLink
              to="/admin"
              onClick={() => handleNavClick()}
              className="flex items-center gap-2 transition-opacity hover:opacity-80"
            >
              <img
                src="/logo.png"
                alt="EasyChat"
                className="h-7 w-auto object-contain"
              />
            </NavLink>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
            title={isCollapsed ? "Expand sidebar" : "Close sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Close sidebar"}
          >
            {/* Mobile close button shows X, desktop shows collapse toggle lines */}
            <span className="md:hidden flex items-center justify-center">
              <X size={18} />
            </span>
            <span className="hidden md:flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </span>
          </button>
        </div>

        {/* Minimal Workspace Switcher Dropdown */}
        <div ref={switcherRef} className={`p-2 border-b border-zinc-100 relative ${isCollapsed ? 'flex justify-center' : ''}`}>
          <button
            onClick={() => setIsSwitcherOpen(!isSwitcherOpen)}
            className={`flex items-center transition-colors border border-zinc-200/70 bg-zinc-50/60 hover:bg-zinc-100/70 active:bg-zinc-100 ${
              isCollapsed ? "w-9 h-9 justify-center rounded-lg p-0" : "w-full justify-between rounded-lg px-2.5 py-2 md:py-1.5"
            }`}
            title={effectiveProduct === "ai-agents" ? "Easy AI Agents" : "EasyFlows"}
          >
            <div className="flex items-center gap-2 min-w-0">
              {effectiveProduct === "ai-agents" ? (
                <div className="w-5 h-5 rounded-md bg-zinc-900 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <Users size={12} strokeWidth={2.2} />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <GitBranch size={12} strokeWidth={2.2} />
                </div>
              )}
              {!isCollapsed && (
                <span className="text-xs font-medium text-zinc-900 truncate">
                  {effectiveProduct === "ai-agents" ? "Easy AI Agents" : "EasyFlows"}
                </span>
              )}
            </div>
            {!isCollapsed && (
              <span className="text-zinc-400 shrink-0 ml-1.5">
                <IconChevron open={isSwitcherOpen} />
              </span>
            )}
          </button>

          {isSwitcherOpen && (
            <div className={`absolute ${isCollapsed ? "left-16 w-52" : "left-2 right-2"} top-[52px] md:top-[48px] bg-white border border-zinc-200 rounded-xl shadow-lg shadow-zinc-900/5 z-50 overflow-hidden p-1 animate-in fade-in zoom-in-95 duration-100`}>
              <button
                onClick={() => handleProductSwitch("ai-agents")}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                  effectiveProduct === "ai-agents" ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <div className="w-5 h-5 rounded-md bg-zinc-900 text-white flex items-center justify-center shrink-0">
                  <Users size={11} />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs truncate">Easy AI Agents</span>
                  <span className="text-[10px] text-zinc-400 font-normal leading-tight">Autonomous AI agents</span>
                </div>
              </button>
              <button
                onClick={() => handleProductSwitch("flows")}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors mt-0.5 ${
                  effectiveProduct === "flows" ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <div className="w-5 h-5 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <GitBranch size={11} />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs truncate">EasyFlows</span>
                  <span className="text-[10px] text-zinc-400 font-normal leading-tight">Visual flow builder</span>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Navigation list */}
        <nav className={`flex-1 overflow-y-auto overflow-x-hidden space-y-3.5 ${
          isCollapsed ? "px-2 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" : "px-2 py-2 scrollbar-thin scrollbar-thumb-zinc-200 scrollbar-track-transparent"
        }`}>
          {effectiveProduct === "flows" ? (
            <div className="space-y-3">
              {isCollapsed ? (
                <div className="flex justify-center">
                  <button
                    onClick={handleCreateNewFlow}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 transition-colors shadow-xs active:scale-95"
                    title="New Flow"
                  >
                    <Plus size={15} strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <div className="px-1">
                  <button
                    onClick={handleCreateNewFlow}
                    className="w-full flex items-center justify-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white py-2 md:py-1.5 rounded-lg text-xs font-medium transition-colors shadow-xs active:scale-[0.99]"
                  >
                    <Plus size={14} />
                    New Flow
                  </button>
                </div>
              )}

              <div className="space-y-0.5">
                {!isCollapsed && (
                  <div className="px-2 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                    <span>Visual Flows</span>
                    <span className="bg-zinc-100 text-zinc-600 px-1.5 py-0.2 rounded text-[9px] font-medium">
                      {flows.length}
                    </span>
                  </div>
                )}
                {loadingFlows ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-zinc-400 border-t-transparent"></div>
                  </div>
                ) : flows.length > 0 ? (
                  flows.map((flow) => {
                    const isActiveFlow = location.search.includes(`id=${flow.id}`) || (location.pathname === `/flow-builder/${flow.id}`);
                    const isMenuOpen = flowMenuOpenId === flow.id;
                    const isLoading = flowActionLoading === flow.id;
                    return (
                      <div key={flow.id} className="relative group flex justify-center">
                        <NavLink
                          to={`/flow-builder?id=${flow.id}`}
                          onMouseEnter={(e) => showItemTooltip(e, flow.name)}
                          onMouseLeave={hideItemTooltip}
                          onClick={() => handleNavClick(`flow-${flow.id}`)}
                          className={`flex items-center rounded-lg text-xs transition-colors ${
                            isCollapsed
                              ? "w-9 h-9 justify-center p-0"
                              : "w-full min-h-[36px] md:min-h-[32px] px-2.5 pr-7"
                          } ${
                            isActiveFlow
                              ? "bg-zinc-900 text-white font-medium shadow-xs"
                              : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100/70"
                          }`}
                        >
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />
                            ) : (
                              <GitBranch
                                size={15}
                                className={isActiveFlow ? "text-white" : "text-zinc-400 group-hover:text-zinc-600"}
                              />
                            )}
                          </div>
                          {!isCollapsed && (
                            <div className="flex-1 overflow-hidden transition-all duration-200 flex items-center justify-between ml-2">
                              <span className="truncate block font-medium capitalize">{flow.name}</span>
                              {flow.is_active && (
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full ml-2 shrink-0"></span>
                              )}
                            </div>
                          )}
                        </NavLink>

                        {/* 3-dot menu button */}
                        {!isCollapsed && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFlowMenuOpenId(isMenuOpen ? null : flow.id);
                            }}
                            className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 md:w-5 md:h-5 flex items-center justify-center rounded transition-all z-10 ${
                              isActiveFlow
                                ? 'text-white/70 hover:text-white hover:bg-white/10'
                                : 'text-zinc-400 hover:text-zinc-800 hover:bg-zinc-200/60 opacity-0 group-hover:opacity-100'
                            } ${isMenuOpen ? 'opacity-100 !text-zinc-800 !bg-zinc-200/60' : ''}`}
                            title="Flow options"
                          >
                            <MoreHorizontal size={12} />
                          </button>
                        )}

                        {/* Dropdown menu */}
                        {isMenuOpen && !isCollapsed && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setFlowMenuOpenId(null)}
                            />
                            <div className="absolute left-2 right-2 z-50 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden p-1 animate-in fade-in zoom-in-95 duration-100"
                              style={{ top: '100%' }}
                            >
                              <button
                                onClick={(e) => handleDuplicateFlow(e, flow)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 rounded-md transition-colors text-left"
                              >
                                <Copy size={12} className="text-zinc-400 shrink-0" />
                                Duplicate
                              </button>
                              <button
                                onClick={(e) => handleDeleteFlow(e, flow.id)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors text-left"
                              >
                                <Trash2 size={12} className="shrink-0" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  !isCollapsed && (
                    <div className="px-2 py-2.5 border border-dashed border-zinc-200 rounded-lg text-[10px] text-zinc-400 text-center">
                      No sequences found
                    </div>
                  )
                )}
              </div>
            </div>
          ) : (
            filteredSections.map((section, secIdx) => {
              const isSectionExpanded = !section.title || expandedSections[section.title];

              return (
                <div key={secIdx} className="space-y-0.5">
                  {/* Minimal Collapsible Section Title */}
                  {section.title && (
                    isCollapsed ? (
                      <div className="w-full flex items-center justify-center py-1.5" key={section.title + "-divider"}>
                        <div className="w-6 border-t border-zinc-200" />
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleSection(section.title)}
                        className="w-full flex items-center justify-between text-[10px] font-semibold text-zinc-400 uppercase tracking-wider px-2.5 py-1.5 md:py-1 rounded-md hover:text-zinc-700 transition-colors"
                      >
                        <span>{section.title}</span>
                        <span className="text-zinc-400">
                          <IconChevron open={isSectionExpanded} />
                        </span>
                      </button>
                    )
                  )}

                  {/* Collapsible Section Items Container */}
                  <div
                    style={{
                      maxHeight: isSectionExpanded ? "2000px" : "0px",
                      overflow: "hidden",
                      transition: "max-height 0.2s ease-in-out",
                    }}
                    className="space-y-0.5"
                  >
                    {section.items.map((item) => {
                      const { id, label, path, icon: Icon, type, hasBadge, subItems } = item;

                      if (type === "single") {
                        return (
                          <NavLink
                            key={id}
                            to={path}
                            onMouseEnter={(e) => showItemTooltip(e, label)}
                            onMouseLeave={hideItemTooltip}
                            onClick={() => handleNavClick(id)}
                            className={({ isActive }) => {
                              const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                              return `group relative flex items-center rounded-lg text-xs transition-colors ${
                                isCollapsed
                                  ? "w-9 h-9 mx-auto justify-center p-0"
                                  : "w-full min-h-[36px] md:min-h-[32px] px-2.5"
                              } ${effectiveActive
                                ? "bg-zinc-900 text-white font-medium shadow-xs"
                                : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100/70"
                              }`;
                            }}
                          >
                            {({ isActive }) => {
                              const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                              return (
                                <>
                                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                    <Icon
                                      size={15}
                                      strokeWidth={effectiveActive ? 2.2 : 2}
                                      className={`transition-colors ${effectiveActive ? "text-white" : "text-zinc-400 group-hover:text-zinc-700"}`}
                                    />
                                  </div>
                                  {!isCollapsed && (
                                    <div className="flex-1 flex items-center justify-between overflow-hidden transition-all duration-200 ml-2.5">
                                      <span className="truncate">{label}</span>
                                      {hasBadge && activeChatsCount > 0 && (
                                        <span className={`flex items-center justify-center px-1.5 py-0.2 text-[9px] font-semibold rounded-full min-w-[16px] ml-1.5 ${
                                          effectiveActive ? "bg-white text-zinc-900" : "bg-zinc-900 text-white"
                                        }`}>
                                          {activeChatsCount}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {/* Live count notification badge in collapsed state */}
                                  {isCollapsed && hasBadge && activeChatsCount > 0 && (
                                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-zinc-900 ring-2 ring-white rounded-full" />
                                  )}
                                </>
                              );
                            }}
                          </NavLink>
                        );
                      }

                      if (type === "dropdown") {
                        const isOpen = isDropdownOpen(id);
                        const dropdownActive = subItems.some(sub => location.pathname === sub.path);

                        return (
                          <React.Fragment key={id}>
                            <button
                              onMouseEnter={(e) => showItemTooltip(e, label)}
                              onMouseLeave={hideItemTooltip}
                              onClick={() => {
                                hideItemTooltip();
                                if (isCollapsed) {
                                  if (setIsCollapsed) {
                                    setIsCollapsed(false);
                                  }
                                  if (id === "sources") {
                                    setSourcesOpen(true);
                                    setExpandedSections(prev => ({ ...prev, "BOT BUILDER": true }));
                                  }
                                  if (id === "workspace") {
                                    setWorkspaceOpen(true);
                                    setExpandedSections(prev => ({ ...prev, "SETTINGS": true }));
                                  }
                                } else {
                                  toggleDropdown(id);
                                }
                              }}
                              className={`group relative flex items-center rounded-lg text-xs transition-colors ${
                                isCollapsed
                                  ? "w-9 h-9 mx-auto justify-center p-0"
                                  : "w-full min-h-[36px] md:min-h-[32px] px-2.5"
                              } ${dropdownActive && !isOpen
                                ? "bg-zinc-100 text-zinc-900 font-medium"
                                : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100/70"
                              }`}
                            >
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                <Icon
                                  size={15}
                                  strokeWidth={dropdownActive ? 2.2 : 2}
                                  className={`transition-colors ${dropdownActive ? "text-zinc-900" : "text-zinc-400 group-hover:text-zinc-700"}`}
                                />
                              </div>
                              {!isCollapsed && (
                                <div className="flex-1 flex items-center justify-between overflow-hidden transition-all duration-200 ml-2.5">
                                  <span className="truncate">{label}</span>
                                  <span className="text-zinc-400 ml-1.5 shrink-0 group-hover:text-zinc-600">
                                    <IconChevron open={isOpen} />
                                  </span>
                                </div>
                              )}
                            </button>

                            {/* Minimal Dropdown Submenu links */}
                            {!isCollapsed && isOpen && (
                              <div className="ml-3 pl-2.5 border-l border-zinc-200 space-y-0.5 mt-0.5 mb-1">
                                {subItems.map((subItem) => {
                                  const subActive = isPathActive(subItem.path);
                                  return (
                                    <NavLink
                                      key={subItem.id}
                                      to={subItem.path}
                                      onClick={() => handleNavClick(subItem.id)}
                                      className={({ isActive }) => {
                                        const effectiveActive = isActive || subActive;
                                        return `group relative flex items-center min-h-[34px] md:min-h-[30px] rounded-md text-xs transition-colors px-2 py-1 ${effectiveActive
                                          ? "bg-zinc-900 text-white font-medium shadow-xs"
                                          : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/60"
                                        }`;
                                      }}
                                    >
                                      {({ isActive }) => {
                                        const effectiveActive = isActive || subActive;
                                        return (
                                          <div className="flex items-center w-full min-w-0">
                                            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mr-2">
                                              <subItem.icon
                                                size={13}
                                                strokeWidth={effectiveActive ? 2.2 : 2}
                                                className={`transition-colors ${effectiveActive ? "text-white" : "text-zinc-400 group-hover:text-zinc-600"}`}
                                              />
                                            </div>
                                            <span className="truncate">{subItem.label}</span>
                                          </div>
                                        );
                                      }}
                                    </NavLink>
                                  );
                                })}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }

                      return null;
                    })}
                  </div>
                </div>
              );
            })
          )}
        </nav>

        {/* Minimal Footer: Sign Out Button */}
        <div className={`border-t border-zinc-100 py-2 shrink-0 ${isCollapsed ? 'flex justify-center px-0' : 'p-2'}`}>
          <button
            onMouseEnter={(e) => showItemTooltip(e, "Sign Out")}
            onMouseLeave={hideItemTooltip}
            onClick={() => {
              hideItemTooltip();
              if (typeof window !== "undefined" && window.innerWidth < 768 && setIsCollapsed) {
                setIsCollapsed(true);
              }
              localStorage.removeItem("user");
              localStorage.removeItem("activeAiAgentId");
              localStorage.removeItem("activeTenantId");
              localStorage.removeItem("activeProduct");
              sessionStorage.removeItem("user");
              sessionStorage.clear();
              window.location.href = "/login";
            }}
            className={`flex items-center rounded-lg text-xs font-normal text-zinc-500 hover:text-red-600 hover:bg-red-50/70 transition-colors group ${
              isCollapsed ? "w-9 h-9 justify-center p-0" : "w-full min-h-[36px] md:min-h-[32px] px-2.5"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <LogOut
                size={15}
                className="text-zinc-400 group-hover:text-red-600 transition-colors"
              />
            </div>
            {!isCollapsed && (
              <div className="flex items-center whitespace-nowrap overflow-hidden transition-all duration-200 ml-2.5">
                Sign Out
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Floating Tooltip Portal for Collapsed Sidebar */}
      {isCollapsed && tooltip.visible && ReactDOM.createPortal(
        <div
          className="fixed z-[99999] pointer-events-none -translate-y-1/2 flex items-center animate-fadeIn"
          style={{ top: `${tooltip.top}px`, left: `${tooltip.left}px` }}
        >
          <div className="relative bg-zinc-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md shadow-xl whitespace-nowrap border border-zinc-800">
            {tooltip.text}
            {/* Arrow */}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[4px] border-transparent border-r-zinc-900" />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
